import Axios, { AxiosRequestConfig } from 'axios';
import * as turf from '@turf/turf';
import { Field, Zone, SoilProfileSet } from '../types/types';
import { getBearerToken, queryStringFromObject } from '../utils/misc';
import { createPolygonFeatureFromZones } from '../utils/mapUtils';
const API_URL = process.env.REACT_APP_LS_API_URL;

const authRequestInterceptor = (config: AxiosRequestConfig) => {
    config.headers!.authorization = getBearerToken();
    config.headers!.Accept = 'application/json';

  return config;
}

export const apiClient = Axios.create({
  baseURL: API_URL,
});

apiClient.interceptors.request.use(authRequestInterceptor);
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    const message = error.response?.data?.message || error.message;
    console.error(message);
    return Promise.reject(error);
  }
);


const allFields = async () : Promise<Field[]> => {
    const queryParams = {
      orderBy: "name",
    }

    const { data } = await apiClient.get<any[]>(`/landscan/data/fields?${queryStringFromObject(queryParams)}`);
    const fields = data.map((f: any) => {

      //console.log("FF041624 f", f);
      return {
        id: f.fieldId,
        entityId: f.entityId,
        name: f.name,
        description: f.description,
        grower_id: f.growerId,
        farm_id: f.farmId,
        crop_id: f.cropId,
        variety_id: f.varietyId,
        plant_spacing: f.plantSpacing,
        row_spacing: f.rowSpacing,
        year_planted: f.yearPlanted,
        enable_level_2: f.enableLevel2,
        enable_level_3: f.enableLevel3,
        area: f.acres,
        url: f.url,
        geom: JSON.parse(f.geom),
        centroid: turf.centroid(JSON.parse(f.geom)),
        stats: {}
      } as Field;
    });

    return fields;
}

const allZones = async (fieldId: number) : Promise<Zone[]> => {
    const queryParams = {
      orderBy: "name",
    }

    const { data } = await apiClient.get<any[]>(`landscan/data/zonesets?fieldId=${fieldId}`);//${queryStringFromObject(queryParams)}`);
    console.log("B041924 data", data);
    const zonesFiltered = data.filter((f: any) => {
      if (f.zones){
        return f;
      }
    })
    console.log("B041924 zonesFiltered", zonesFiltered);
    const zonesJob = zonesFiltered.map((f: any) => {
      //console.log("041924 f", f);
      return {
        zonesetId: f.zonesetId,
        fieldId: f.fieldId,
        entityId: f.entityId,
        name: f.name,
        description: f.description,
        zonetypeId: f.zonetypeId,
        orgId: f.orgId,
        zones: f.zones,
        s3Uri: f.s3Uri,
        createdAt: f.createdAt,
        updatedAt: f.updatedAt,
        geojson: createPolygonFeatureFromZones(f.zones),
      } as Zone;
    });
    console.log("B041924 zonesJob", zonesJob);
    return zonesJob;
}

const allZonesPrelim = async (fieldId: number) : Promise<Zone[]> => {

    const { data } = await apiClient.get<any[]>(`landscan/data/zoneSetsPrelim?fieldId=${fieldId}`);//${queryStringFromObject(queryParams)}`);
    console.log("PRELIM 041924 data", data);
    const zonesJob = data.map((f: any) => {
      //console.log("PRELIM 041924 f", f);
      return {
        zonesetId: f.zonesetId,
        fieldId: f.fieldId,
        entityId: f.entityId,
        name: f.name,
        description: f.description,
        zonetypeId: f.zonetypeId,
        orgId: f.orgId,
        zones: f.zones,
        createdAt: f.createdAt,
        updatedAt: f.updatedAt,
        geojson: f.geojson, //createPolygonFeatureFromZones(f.zones),
      } as Zone;
    });
    console.log("PRELIM 041924 zonesJob", zonesJob);
    return zonesJob;
}

const pollZoneCreationStatus = async (jobId: number) : Promise<any> => {
    //const data  = await apiClient.get<any[]>(`landscan/jobs/${jobId}`);
    const data  = await apiClient.get<any[]>(`landscan/jobs/watch?topJobId=${jobId}&failStatusOnFailedChildren=true`);
    //console.log("040524 data", data);
    return data;
}

const createZoneSetPrelim = async (params: any) : Promise<any> => {

    console.log("B042224 params",params);
    console.log("B042224 queryStringFromObject(params)",queryStringFromObject(params));
    const data = await apiClient.post<any[]>(`landscan/data/zonesetsPrelim`,
      queryStringFromObject(params)
    );
    console.log("040124 data", data);
    return data;
}

const unpublishZoneSet = async (zoneSetId: string ) : Promise<any> => {
    //const data = await apiClient.post<any[]>(`processing/data/zonesetsPrelim/publish`, // ?
    const data = await apiClient.post<any[]>(`/landscan/data/zoneSets/unpublish`, // or 
      {"zonesetId": zoneSetId}
    );
    console.log("040124 data", data);
    return data;
}

const publishZoneSet = async (prelimZoneSetId: string ) : Promise<any> => {
    //const data = await apiClient.post<any[]>(`processing/data/zonesetsPrelim/publish`, // ?
    const data = await apiClient.post<any[]>(`/landscan/data/zoneSetsPrelim/publish`, // or 
      JSON.stringify({"zonesetsPrelimId": prelimZoneSetId}),
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    console.log("040124 data", data);
    return data;
}

const deleteZoneSetPrelim = async (prelimZoneSetId: string ) : Promise<any> => {
    // /processing/zonesetsPrelim/{zonesetsPrelimId}
    // /landscan/data/zonesetsPrelim/{zonesetPrelimId}
    const data = await apiClient.delete<any[]>(`landscan/data/zonesetsPrelim/${prelimZoneSetId}`);
    console.log("C040624 data", data);
    return data;
}

const getFieldProfileSets = async (fieldId: number, year?: number): Promise<SoilProfileSet[]> => {
  const queryParams = {
    fieldId: fieldId,
    expandObject: true,
    dateRange: year ? `${year}-01-01/${year}-12-31` : undefined // use: `2000-01-01/${year}-12-31` to get all available years and display in 
  }

  const { data } = await apiClient.get<SoilProfileSet[]>(`/landscan/data/soilprofilesets?${queryStringFromObject(queryParams)}`);

  return data;
}

const getTextCsv = async (url: string): Promise<string> => {
  const newUrl = url.replace(API_URL!, '');

  const data = await apiClient.get(newUrl, {
    responseType: 'text'
  });

  return data.toString();
}

const LandscanService = {
    allFields,
    allZones,
    allZonesPrelim,
    createZoneSetPrelim,
    pollZoneCreationStatus,
    deleteZoneSetPrelim,
    getFieldProfileSets,
    publishZoneSet,
    unpublishZoneSet,
    getTextCsv,
    apiClient
}

export default LandscanService;
